import { gql, useMutation, useQuery } from '@apollo/client'
import { useMemo } from 'react'

import {
  GetFavoriteMemberQuery,
  GetFavoriteMemberQueryVariables,
  UpdateFavoriteMemberMutation,
  UpdateFavoriteMemberMutationVariables,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import {
  useCurrentUser,
  useMessage,
  useRouter,
  useTranslation,
  useUserTmpUploader,
} from '@/core'
import { makeViewModel } from '@/core/architecture'
import { Camera } from '@/core/native'
import { createUuid } from '@/lib/uuid'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { user } = useCurrentUser()
  const { showError, showMessage } = useMessage()
  const { t } = useTranslation('consoleFavoriteMemberEdit')
  const { routerPages, router } = useRouter<{ favoriteMemberId: string }>()
  const { getImage } = Camera
  const { upload, progress } = useUserTmpUploader(user?.uid ?? '')

  const favoriteMemberId = router.query.favoriteMemberId

  const { data, loading, error } = useQuery<
    GetFavoriteMemberQuery,
    GetFavoriteMemberQueryVariables
  >(GET_FAVORITE_MEMBER_QUERY, {
    variables: {
      inputs: {
        favoriteMemberId: favoriteMemberId,
      },
    },
  })

  const favoriteMember = useMemo(() => {
    return data?.artistFavoriteMembers?.[0]
      ? {
          favoriteMemberId: data.artistFavoriteMembers[0].id,
          name: data.artistFavoriteMembers[0].name,
          avatar: data.artistFavoriteMembers[0].avatar?.uri ?? '',
        }
      : undefined
  }, [data?.artistFavoriteMembers])

  const [updateFavoriteMember, { loading: updatingMember }] = useMutation<
    UpdateFavoriteMemberMutation,
    UpdateFavoriteMemberMutationVariables
  >(UPDATE_FAVORITE_MEMBER_MUTATION)

  return {
    state: {
      artistId: user?.uid ?? '',
      progress,
      favoriteMemberId: favoriteMemberId || '',
      favoriteMember,
      updatingMember,
      loading,
    },
    actions: {
      async onDrop() {
        try {
          const file = await getImage(config.upload.mimeTypes.image)

          if (file.size > config.upload.limit['image']) {
            throw new Error('uploadErrorMaximumSize')
          }

          const { tmpPath, fileUrl } = await upload(file, false, true)
          const uuid = createUuid()

          return {
            id: uuid,
            resourceName: uuid,
            imageUploadPath: tmpPath,
            imageUrl: fileUrl,
          }
        } catch (error) {
          if (error instanceof Error) {
            if (error.message === 'uploadErrorMaximumSize') {
              showError(new Error(t('uploadErrorMaximumSize')))
            } else if (error.message === 'uploadErrorImageOnly') {
              showError(new Error(t('uploadErrorImageOnly')))
            } else {
              showError(error)
            }
          } else {
            showError(error as Error)
          }
          return null
        }
      },

      async onSubmit(values) {
        await updateFavoriteMember({
          variables: {
            inputs: {
              id: favoriteMemberId,
              avatarPath: values.avatarPath || '',
              name: values.name,
            },
          },
        }).catch(error => {
          showError(error)
          throw error
        })

        showMessage(t('updateSuccess'), 'success')
        // 更新成功後、推しメンバー一覧に遷移
        router.goBack(routerPages.ConsoleFavoriteMemberSettings)
      },
    },
  }
})

const UPDATE_FAVORITE_MEMBER_MUTATION = gql`
  mutation UpdateFavoriteMember($inputs: ArtistUpdateFavoriteMemberInput!) {
    artistUpdateFavoriteMember(inputs: $inputs) {
      id
      name
      avatar {
        uri
      }
      orderNumber
    }
  }
`

const GET_FAVORITE_MEMBER_QUERY = gql`
  query GetFavoriteMember($inputs: ArtistFavoriteMembersInput!) {
    artistFavoriteMembers(inputs: $inputs) {
      id
      name
      avatar {
        uri
      }
    }
  }
`
